import { ZodObject, ZodRawShape, ZodSchema } from "zod";
import {
    useWorkspaceUserCache,
    WORKSPACE_USER_CACHE_KEYS,
} from "./useWorkspaceUserCache";
import {
    useOrganizationUserCache,
    ORGANIZATION_USER_CACHE_KEYS,
} from "./useOrganizationUserCache";

type ScopedCacheKey = WORKSPACE_USER_CACHE_KEYS | ORGANIZATION_USER_CACHE_KEYS;

interface UseScopedCacheReturn<T> {
    cachedValue: T | undefined;
    setCachedValue: (value: T) => void;
    isOrganizationScope: boolean;
    isWorkspaceScope: boolean;
}

export function useScopedCache<T>(
    cacheKey?: ScopedCacheKey,
    zodSchema?: ZodSchema<T> | ZodObject<ZodRawShape>,
): UseScopedCacheReturn<T> {
    const isOrganizationScope = cacheKey
        ? Object.values(ORGANIZATION_USER_CACHE_KEYS).includes(
              cacheKey as ORGANIZATION_USER_CACHE_KEYS,
          )
        : false;

    const isWorkspaceScope = cacheKey
        ? Object.values(WORKSPACE_USER_CACHE_KEYS).includes(
              cacheKey as WORKSPACE_USER_CACHE_KEYS,
          )
        : false;

    const [workspaceCachedValue, setWorkspaceCachedValue] =
        useWorkspaceUserCache<T>(
            isWorkspaceScope
                ? (cacheKey as WORKSPACE_USER_CACHE_KEYS)
                : undefined,
            zodSchema,
        );

    const [orgCachedValue, setOrgCachedValue] = useOrganizationUserCache<T>(
        isOrganizationScope
            ? (cacheKey as ORGANIZATION_USER_CACHE_KEYS)
            : undefined,
        zodSchema,
    );

    const cachedValue = isOrganizationScope
        ? orgCachedValue
        : workspaceCachedValue;

    const setCachedValue = isOrganizationScope
        ? setOrgCachedValue
        : setWorkspaceCachedValue;

    return {
        cachedValue,
        setCachedValue,
        isOrganizationScope,
        isWorkspaceScope,
    };
}
